import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { DateTime } from 'luxon';

import { Period } from 'dashboard/lib/unixTime';
import {
  MetricsApiLogPathDatum,
  MetricsApiLogPaths,
  MetricsApiLogVolumeOverTime,
} from 'dashboard/models/metrics-api-logs';
import { Iso8601DateString } from 'lib/types';

const NOW = DateTime.local();

export interface MetricsApiLogFilterParams {
  createdAtStart?: Iso8601DateString;
  createdAtEnd?: Iso8601DateString;
  apiPaths: MetricsApiLogPathDatum[];
}

interface MetricsApiLogVolumeOverTimePayload {
  apiLogsVolumeOverTime: MetricsApiLogVolumeOverTime;
  queriedAt: Iso8601DateString;
}

interface MetricsApiLogPathsPayload {
  apiLogsPaths?: MetricsApiLogPaths;
  queriedAt: Iso8601DateString;
}

export interface ApiLogsMetricsState {
  filters: MetricsApiLogFilterParams;
  apiLogsVolumeOverTime: MetricsApiLogVolumeOverTime;
  apiLogsPaths: MetricsApiLogPaths;
}

export const initialState: ApiLogsMetricsState = {
  filters: {
    createdAtStart: NOW.startOf('day').toISO(),
    createdAtEnd: NOW.endOf('day').toISO(),
    apiPaths: [],
  },
  apiLogsVolumeOverTime: {
    data: [],
    domain: [NOW.startOf('day').toUnixInteger(), NOW.endOf('day').toUnixInteger()],
    period: Period.Hour,
    updated_at: NOW.toISO(),
  },
  apiLogsPaths: {
    data: [],
  },
};

const getInitialState = (): ApiLogsMetricsState => {
  const currentTime = DateTime.local();
  return {
    ...initialState,
    filters: {
      ...initialState.filters,
      createdAtStart: currentTime.startOf('day').toISO(),
      createdAtEnd: currentTime.endOf('day').toISO(),
    },
    apiLogsVolumeOverTime: {
      ...initialState.apiLogsVolumeOverTime,
      domain: [
        currentTime.startOf('day').toUnixInteger(),
        currentTime.endOf('day').toUnixInteger(),
      ],
    },
  };
};

const { reducer, actions } = createSlice({
  name: 'metrics-api-logs',
  initialState,
  reducers: {
    setMetricsApiLogsVolumeOverTime: (
      state,
      action: PayloadAction<MetricsApiLogVolumeOverTimePayload>,
    ) => {
      state.apiLogsVolumeOverTime = action.payload.apiLogsVolumeOverTime;
      return state;
    },
    setMetricsApiLogsPaths: (state, action: PayloadAction<MetricsApiLogPathsPayload>) => {
      const { ...apiLogsPaths } = action.payload.apiLogsPaths;
      state.apiLogsPaths = apiLogsPaths;
      return state;
    },
    clearMetrics: (state) => {
      return { ...getInitialState(), filters: state.filters };
    },
    setMetricsApiLogFilters: (state, action: PayloadAction<Partial<MetricsApiLogFilterParams>>) => {
      const newFilters = {
        ...state.filters,
        ...action.payload,
      };

      return {
        ...state,
        filters: newFilters,
      };
    },
    resetMetricsDateFilter: () => {
      return {
        ...getInitialState(),
      };
    },
  },
});

export const {
  setMetricsApiLogsVolumeOverTime,
  setMetricsApiLogsPaths,
  clearMetrics,
  setMetricsApiLogFilters,
  resetMetricsDateFilter,
} = actions;

export const metricsApiLogsReducer = reducer;
